import React, { useState } from 'react'
import { ChevronDown, ChevronRight, PlugZap, Search, Unplug } from 'lucide-react'
import {
  Clock,
  GitBranch,
  Split,
  Terminal,
  Settings,
  Timer,
  Upload,
  Edit3,
  Network,
  Cloud,
  RotateCw
} from 'lucide-react'
import ActivityCard from './ActivityCard'
import { ACTIVITY_TYPES } from '../helpers'

interface ActivityGroup {
  title: string
  activities: Array<{
    type: string
    title: string
    description: string
    icon: React.ComponentType<any>
  }>
}

interface ActivitySidebarProps {
  customActivities: any[]
  onAddToWorkflow?: (activity: any) => void
}

const ActivitySidebar: React.FC<ActivitySidebarProps> = ({ customActivities, onAddToWorkflow }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<string[]>([])

  // Activities using backend types directly - no conversion needed
  const activityGroups: ActivityGroup[] = [
    ...(customActivities && customActivities.length > 0 ? [{
      title: 'Activities',
      activities: customActivities.map(activity => ({
        type: activity.type || activity.type,
        title: activity.title || activity.name || activity.title,
        description: activity.description || activity.title,
        icon: activity.icon || Settings,
      }))
    }] : []),
    {
      title: 'HTTP',
      activities: [
        {
          type: ACTIVITY_TYPES.HTTP_ENDPOINT,
          title: 'HTTP Endpoint',
          description: 'Create HTTP endpoint',
          icon: Cloud,
        },
        {
          type: ACTIVITY_TYPES.FLOW_SEND_HTTP_REQUEST,
          title: 'HTTP Request (flow)',
          description: 'HTTP request with flow control',
          icon: Network,
        },
        {
          type: ACTIVITY_TYPES.WRITE_HTTP_RESPONSE,
          title: 'HTTP Response',
          description: 'Send HTTP response',
          icon: Upload,
        },
      ]
    },
    {
      title: 'Primitives',
      activities: [
        {
          type: ACTIVITY_TYPES.SET_VARIABLE,
          title: 'Set Variable',
          description: 'Set a variable in the workflow',
          icon: Clock,
        },
        {
          type: ACTIVITY_TYPES.EVENT,
          title: 'Event',
          description: 'Trigger an event in the workflow',
          icon: PlugZap,
        },
      ]
    },
    {
      title: 'Scheduling',
      activities: [
        {
          type: ACTIVITY_TYPES.CRON,
          title: 'Cron',
          description: 'Schedule using cron expressions',
          icon: Clock,
        },
        {
          type: ACTIVITY_TYPES.DELAY,
          title: 'Delay',
          description: 'Add delay to workflow execution',
          icon: Timer,
        },
        {
          type: ACTIVITY_TYPES.TIMER,
          title: 'Timer',
          description: 'Set up timer-based execution',
          icon: Timer,
        },
        {
          type: ACTIVITY_TYPES.START_AT,
          title: 'Start At',
          description: 'Set the start time for the workflow',
          icon: Timer,
        },
      ]
    },
    {
      title: 'Looping',
      activities: [
        {
          type: ACTIVITY_TYPES.WHILE,
          title: 'While',
          description: 'Create loops with condition in workflow',
          icon: RotateCw,
        },
        {
          type: ACTIVITY_TYPES.BREAK,
          title: 'Break',
          description: 'Exit the current loop or switch',
          icon: Split,
        },
      ]
    },
    {
      title: 'Branching',
      activities: [
        {
          type: ACTIVITY_TYPES.IF,
          title: 'If',
          description: 'Create conditional branches in workflow',
          icon: GitBranch,
        },
        {
          type: ACTIVITY_TYPES.SWITCH,
          title: 'Switch',
          description: 'Multiple condition branching',
          icon: Split,
        },
      ]
    },
    {
      title: 'Composition',
      activities: [
        {
          type: ACTIVITY_TYPES.DISPATCH_WORKFLOW,
          title: 'Dispatch Workflow',
          description: 'Run another workflow',
          icon: Unplug,
        },
        {
          type: ACTIVITY_TYPES.SET_OUTPUT,
          title: 'Set Output',
          description: 'Set the output of the workflow',
          icon: Unplug,
        }
      ]
    },
    {
      title: 'Console',
      activities: [
        {
          type: ACTIVITY_TYPES.READ_LINE,
          title: 'Read Line',
          description: 'Read line from console input',
          icon: Terminal,
        },
        {
          type: ACTIVITY_TYPES.WRITE_LINE,
          title: 'Write Line',
          description: 'Write line to console output',
          icon: Edit3,
        },
      ]
    },
  ]

  const toggleGroup = (groupTitle: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupTitle)
        ? prev.filter(title => title !== groupTitle)
        : [...prev, groupTitle]
    )
  }

  const filteredGroups = activityGroups.map(group => ({
    ...group,
    activities: group.activities.filter(activity =>
      activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(group => group.activities.length > 0)

  return (
    <div className="w-56 bg-white border-r border-slate-200 dark:bg-zink-700 dark:border-zink-600 flex flex-col h-full">
      {/* Search Header */}
      <div className="p-4 border-b border-slate-200 dark:border-zink-600">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-custom-500 focus:border-transparent dark:bg-zink-600 dark:border-zink-500 dark:text-zink-200"
          />
        </div>
      </div>

      {/* Scrollable Activity Groups */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-2">
          {filteredGroups.map((group) => (
            <div key={group.title} className="mb-2">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(group.title)}
                className="w-full flex items-center justify-between p-2 text-sm font-medium text-slate-700 dark:text-zink-200 hover:bg-slate-50 dark:hover:bg-zink-600 rounded-lg transition-colors"
              >
                <span>{group.title}</span>
                {expandedGroups.includes(group.title) ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>

              {/* Group Activities */}
              {expandedGroups.includes(group.title) && (
                <div className="mt-2 space-y-2 pl-2">
                  {group.activities.map((activity, index) => (
                    <div key={`${group.title}-${index}`} className="transform scale-95">
                      <ActivityCard
                        {...activity}
                        onAddToWorkflow={onAddToWorkflow}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ActivitySidebar
